<script lang="ts">
  import type { Snippet } from 'svelte';

  /**
   * Modal générique réutilisable basé sur `<dialog>` natif.
   *
   * **Aucun style dans ce composant** — toutes les classes (`modal`,
   * `modal__inner`, etc.) sont définies globalement dans
   * `src/css/components/modal.css`. Idem pour les boutons (`.btn`,
   * `.btn--primary`, etc. dans `btn.css`) qu'on consomme via le slot
   * `actions`.
   *
   * Tous les dialogs métier (`ExportDialog`, `NewPatternDialog`, etc.)
   * doivent wrap ce composant. Plus de `<dialog>` direct dans les feature
   * components.
   *
   * Mobile-friendly (cf. modal.css) :
   *  - < 600px : pleine largeur ancrée en bas (ergonomie pouce)
   *  - safe-area-inset-bottom respecté
   *  - max-height 100dvh avec scroll interne du body
   *
   * Props :
   *  - `open` : pilote l'affichage (state du parent)
   *  - `title` : optionnel, affiché dans le header
   *  - `showClose` : bouton ✕ dans le header (défaut true)
   *  - `blocking` : Escape + backdrop click n'ont pas d'effet — pour les
   *    confirmations critiques (l'user DOIT choisir une action)
   *  - `maxWidth` : largeur max desktop en px (défaut 460)
   *  - `onClose` : callback de fermeture (Escape, backdrop, bouton ✕)
   *  - `children` : contenu principal
   *  - `actions` : snippet footer (boutons d'action)
   */

  type Props = {
    open: boolean;
    title?: string;
    showClose?: boolean;
    blocking?: boolean;
    maxWidth?: number;
    /** Hauteur fixe (px) pour les dialogs dont la taille ne doit pas
     *  varier avec le contenu (ex : guide perles avec search filter qui
     *  ferait sauter la modale à chaque frappe). Sur mobile, le @media
     *  query reprend la main pour le fullscreen. */
    height?: number;
    onClose: () => void;
    children?: Snippet;
    actions?: Snippet;
  };

  let {
    open,
    title,
    showClose = true,
    blocking = false,
    maxWidth = 460,
    height,
    onClose,
    children,
    actions,
  }: Props = $props();

  let dialogEl: HTMLDialogElement | undefined = $state();

  $effect(() => {
    if (!dialogEl) return;
    if (open && !dialogEl.open) {
      dialogEl.showModal();
    } else if (!open && dialogEl.open) {
      dialogEl.close();
    }
  });

  function onDialogClose(): void {
    if (open) onClose();
  }

  function onCancel(e: Event): void {
    // L'event cancel se déclenche sur Escape avant close. Si blocking,
    // on bloque pour empêcher la fermeture par Escape.
    if (blocking) e.preventDefault();
  }

  function onBackdropClick(e: MouseEvent): void {
    if (blocking) return;
    if (e.target === dialogEl) onClose();
  }
</script>

<dialog
  bind:this={dialogEl}
  class="modal"
  class:modal--fixed-height={height !== null}
  style:--modal-max-width="{maxWidth}px"
  style:--modal-height={height !== null ? `${height}px` : null}
  onclose={onDialogClose}
  oncancel={onCancel}
  onclick={onBackdropClick}
>
  <div class="modal__inner">
    {#if title || showClose}
      <header class="modal__header">
        {#if title}<h2 class="modal__title">{title}</h2>{:else}<div></div>{/if}
        {#if showClose}
          <button
            type="button"
            class="modal__close"
            aria-label="Fermer"
            onclick={onClose}
          >✕</button>
        {/if}
      </header>
    {/if}

    <div class="modal__body">
      {@render children?.()}
    </div>

    {#if actions}
      <footer class="modal__actions">
        {@render actions()}
      </footer>
    {/if}
  </div>
</dialog>
